/**
 * Route matching utilities
 *
 * Centralized logic for matching paths against route patterns,
 * extracting parameters, and building paths with parameters.
 */

import type React from 'react';

import { parsePreviewHref as parsePreviewLink, PREVIEW_TYPES } from '@elara/domain/schemas/preview-schemas';
import type { PreviewType } from '@elara/domain/schemas/preview-schemas';

/**
 * Preview types supported in route hash fragments.
 *
 * Re-exported from the canonical `@elara/domain` vocabulary so the client,
 * server prompt, and domain schemas share one source of truth.
 */
export type { PreviewType } from '@elara/domain/schemas/preview-schemas';

export interface RouteMatch {
  params: Record<string, string>;
}

/**
 * Valid preview types for runtime validation.
 *
 * @deprecated Prefer `isPreviewType` / `PREVIEW_TYPES` from `@elara/domain/schemas/preview-schemas`.
 */
export const VALID_PREVIEW_TYPES: readonly PreviewType[] = PREVIEW_TYPES;

/**
 * Match a path against a route pattern
 *
 * @param path - The path to match (e.g., "/trip/123")
 * @param pattern - The route pattern (e.g., "/trip/:id")
 * @returns Match result with extracted parameters or null if no match
 */
export function matchPath(path: string, pattern: string): RouteMatch | null {
  // Handle wildcard route
  if (pattern === '*') {
    return { params: {} };
  }

  const patternParts = pattern.split('/');
  const pathParts = path.split('/');

  // Check if the pattern ends with a wildcard segment (e.g., '/admin/*')
  const hasWildcardSuffix = patternParts.length > 0 && patternParts.at(-1) === '*';

  if (hasWildcardSuffix) {
    // Wildcard suffix: path must have at least as many segments as pattern (minus the wildcard)
    const prefixLength = patternParts.length - 1;
    if (pathParts.length < prefixLength) {
      return null;
    }
    // Verify all prefix segments match
    const params: Record<string, string> = {};
    for (let i = 0; i < prefixLength; i++) {
      const patternPart = patternParts[i]!;
      const pathPart = pathParts[i];
      if (patternPart.startsWith(':')) {
        const paramName = patternPart.slice(1);
        try {
          params[paramName] = decodeURIComponent(pathPart || '');
        } catch {
          params[paramName] = pathPart || '';
        }
      } else if (patternPart !== pathPart) {
        return null;
      }
    }
    // Capture the remaining path as the '*' param
    params['*'] = pathParts.slice(prefixLength).join('/');
    return { params };
  }

  if (patternParts.length !== pathParts.length) {
    return null;
  }

  const params: Record<string, string> = {};

  for (const [i, patternPart] of patternParts.entries()) {
    const pathPart = pathParts[i];

    if (patternPart.startsWith(':')) {
      // Dynamic parameter - extract, decode, and store
      const paramName = patternPart.slice(1);
      try {
        params[paramName] = decodeURIComponent(pathPart || '');
      } catch (error) {
        // If decoding fails (malformed URI), use the raw value
        console.warn(`[route-utils] Failed to decode parameter "${paramName}": ${pathPart}`, error);
        params[paramName] = pathPart || '';
      }
    } else if (patternPart !== pathPart) {
      // Static segment doesn't match
      return null;
    }
  }

  return { params };
}

/**
 * Build a path with parameters
 *
 * Substitutes route parameters (e.g., `:id`) in a path pattern with actual values.
 * Parameter values are URL-encoded for safety.
 * Logs an error if required parameters are missing.
 *
 * @param path - The path template (e.g., "/trip/:id")
 * @param params - Parameters to substitute
 * @returns The built path with parameters substituted and encoded
 *
 * @example
 * buildPathWithParams('/trip/:id', { id: '123' }) // => '/trip/123'
 * buildPathWithParams('/trip/:id', { id: 'hello world' }) // => '/trip/hello%20world'
 */
export function buildPathWithParams(path: string, params: Record<string, string>): string {
  let builtPath = path;
  const missingParams: string[] = [];

  // Find all :param patterns in the path
  const paramPattern = /:(\w+)/g;
  const matches = path.matchAll(paramPattern);

  for (const match of matches) {
    const paramName = match[1];
    if (!paramName) continue; // Skip if no capture group

    const paramValue = params[paramName];

    if (paramValue === undefined || paramValue === null || paramValue === '') {
      missingParams.push(paramName);
    } else {
      builtPath = builtPath.replace(`:${paramName}`, encodeURIComponent(paramValue));
    }
  }

  if (missingParams.length > 0) {
    console.error(`[route-utils] Missing required parameters: ${missingParams.join(', ')} for path: ${path}`, {
      path,
      params,
      missingParams,
    });
  }

  return builtPath;
}

/**
 * Extract route parameters from path using registered route patterns
 *
 * @param path - The path to extract parameters from
 * @param routePatterns - Array of route patterns to try matching against
 * @returns Extracted parameters
 */
export function extractParams(path: string, routePatterns: string[]): Record<string, string> {
  const params: Record<string, string> = {};

  // Find matching route pattern and extract params
  for (const routePattern of routePatterns) {
    const match = matchPath(path, routePattern);
    if (match) {
      Object.assign(params, match.params);
      break;
    }
  }

  return params;
}

/**
 * Parse a hash fragment into key-value pairs
 *
 * @param hash - The hash fragment (e.g., "#preview=destination&id=xxx" or "preview=destination&id=xxx")
 * @returns Object with parsed key-value pairs
 *
 * @example
 * parseHashFragment("#preview=destination&id=123") // => { preview: "destination", id: "123" }
 * parseHashFragment("preview=hotel&id=456") // => { preview: "hotel", id: "456" }
 */
export function parseHashFragment(hash: string): Record<string, string> {
  if (!hash) return {};

  // Strip leading # if present
  const hashWithoutPrefix = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!hashWithoutPrefix) return {};

  const params: Record<string, string> = {};

  try {
    const pairs = hashWithoutPrefix.split('&');
    for (const pair of pairs) {
      const [key, value] = pair.split('=');
      if (key && value !== undefined) {
        try {
          params[decodeURIComponent(key)] = decodeURIComponent(value);
        } catch (error) {
          // If decoding fails, use raw values
          console.warn(`[route-utils] Failed to decode hash param "${key}": ${value}`, error);
          params[key] = value;
        }
      }
    }
  } catch (error) {
    console.warn('[route-utils] Failed to parse hash fragment:', hash, error);
    return {};
  }

  return params;
}

/**
 * Build a hash fragment from key-value pairs
 *
 * @param params - Object with key-value pairs to encode
 * @returns Formatted hash string (e.g., "#preview=destination&id=xxx")
 *
 * @example
 * buildHashFragment({ preview: "destination", id: "123" }) // => "#preview=destination&id=123"
 * buildHashFragment({ preview: "hotel", id: "hello world" }) // => "#preview=hotel&id=hello%20world"
 */
export function buildHashFragment(params: Record<string, string>): string {
  const pairs: string[] = [];

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }

  return pairs.length > 0 ? `#${pairs.join('&')}` : '';
}

/**
 * Minimal route interface for URL building utilities
 */
export interface RouteLike {
  path: string;
  query: Record<string, string>;
  hash?: string;
}

/**
 * Build a URL string from a route object
 *
 * @param route - The route object containing path, query, and optional hash
 * @returns Built URL string
 *
 * @example
 * buildUrlFromRoute({ path: '/trip/123', query: { tab: 'hotels' }, hash: '#filter=active' })
 * // => '/trip/123?tab=hotels#filter=active'
 */
export function buildUrlFromRoute(route: RouteLike): string {
  let url = route.path;
  if (Object.keys(route.query).length > 0) {
    const searchParams = new URLSearchParams(route.query);
    url += `?${searchParams.toString()}`;
  }
  if (route.hash) {
    url += route.hash;
  }
  return url;
}

/**
 * Build a URL string with modified hash and/or query parameters, preserving existing values.
 *
 * @param route - The base route object
 * @param hashParams - Hash parameters to add or update (merged with existing hash params)
 * @param queryParams - Optional query parameters to add or update (merged with existing query params)
 * @returns Built URL string
 *
 * @example
 * const route = { path: '/trip/456', query: { tab: 'hotels' }, hash: '#filter=active' };
 * buildUrlWithParams(route, { preview: 'destination', id: '123' })
 * // => '/trip/456?tab=hotels#filter=active&preview=destination&id=123'
 *
 * buildUrlWithParams(route, { preview: 'hotel' }, { view: 'grid' })
 * // => '/trip/456?tab=hotels&view=grid#filter=active&preview=hotel'
 */
export function buildUrlWithParams(
  route: RouteLike,
  hashParams: Record<string, string>,
  queryParams?: Record<string, string>,
): string {
  // Merge query params if provided
  const mergedQuery = queryParams ? { ...route.query, ...queryParams } : route.query;

  // Merge hash params with existing ones
  const existingHashParams = route.hash ? parseHashFragment(route.hash) : {};
  const mergedHashParams = { ...existingHashParams, ...hashParams };
  const newHash = buildHashFragment(mergedHashParams);

  // Build modified route
  const modifiedRoute: RouteLike = {
    ...route,
    query: mergedQuery,
    hash: newHash || undefined,
  };

  return buildUrlFromRoute(modifiedRoute);
}

/**
 * Determines if a mouse event should be handled by the browser natively
 * (e.g., opening links in new tabs with modifier keys).
 *
 * Returns true if the browser should handle the event (modifier keys pressed,
 * middle click, etc.), false if the application should handle it.
 *
 * @param event - The mouse event to check
 * @returns True if browser should handle, false if app should handle
 *
 * @example
 * // In a link click handler:
 * if (shouldLetBrowserHandleClick(e)) {
 *   return; // Let browser open in new tab, etc.
 * }
 * e.preventDefault();
 * // Handle navigation in app
 */
export function shouldLetBrowserHandleClick(event: React.MouseEvent): boolean {
  return (
    event.defaultPrevented ||
    event.button !== 0 || // Not left click
    event.metaKey || // Cmd+Click (Mac)
    event.altKey || // Alt+Click
    event.ctrlKey || // Ctrl+Click (Windows/Linux)
    event.shiftKey // Shift+Click
  );
}

/**
 * Parse a preview link href to extract the preview type and ID.
 *
 * @param href - The href to parse (e.g., "#preview=destination&id=123")
 * @returns Object with previewType and id, or null if not a valid preview link
 *
 * @example
 * parsePreviewHref("#preview=destination&id=123") // => { previewType: "destination", id: "123" }
 * parsePreviewHref("#preview=hotel&id=456") // => { previewType: "hotel", id: "456" }
 * parsePreviewHref("#other=value") // => null
 */
export function parsePreviewHref(href: string): { previewType: PreviewType; id: string } | null {
  const target = parsePreviewLink(href);
  return target ? { previewType: target.type, id: target.id } : null;
}

/**
 * Classifies a markdown link href so web and mobile renderers handle it identically: an in-app
 * entity **preview** (`#preview=<type>&id=<id>`), an **external** absolute http(s) URL, or an
 * **internal** app path. DOM-free (no `window`), so both platforms share it.
 */
export type ClassifiedMarkdownLink =
  | { kind: 'preview'; previewType: PreviewType; id: string }
  | { kind: 'external'; href: string }
  | { kind: 'internal'; href: string };

export function classifyMarkdownLink(href: string | undefined): ClassifiedMarkdownLink {
  const target = href ? parsePreviewHref(href) : null;
  if (target) return { kind: 'preview', previewType: target.previewType, id: target.id };
  if (href && /^https?:\/\//i.test(href)) return { kind: 'external', href };
  return { kind: 'internal', href: href ?? '' };
}
